import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import {
  glassCardStyles,
  gradientTextStyles,
  Page,
  theme,
  typography,
} from "../Homepage/styles";

const cardLinkNoUnderline = css`
  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
    color: inherit;
  }

  &:hover *,
  &:focus *,
  &:active *,
  &:visited * {
    text-decoration: none;
  }
`;

export const FourYearPage = styled(Page)`
  background: hsl(var(--hp-background));
  background-image: none;
`;

export const FourYearHero = styled.section`
  position: relative;
  padding: 104px 16px 40px;
  overflow: hidden;
  text-align: center;

  @media (min-width: 768px) {
    padding: 128px 24px 48px;
  }
`;

export const FourYearHeroBgGradient = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom right,
    hsl(var(--hp-primary) / 0.1) 0%,
    hsl(var(--hp-background)) 50%,
    hsl(var(--hp-accent) / 0.05) 100%
  );
`;

export const FourYearHeroBgRadial = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at center,
    hsl(var(--hp-primary) / 0.15) 0%,
    transparent 70%
  );
`;

export const FourYearHeroInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 48rem;
  margin: 0 auto;
`;

export const FourYearKicker = styled.span`
  display: inline-flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 4px 12px;
  border-radius: 9999px;
  background: hsl(var(--hp-primary) / 0.15);
  color: ${theme.primary};
  font-size: 0.75rem;
  font-weight: ${typography.weights.semibold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const FourYearHeroTitle = styled.h1`
  margin: 0 0 24px;
  color: ${theme.foreground};
  font-size: 2.25rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.2;

  @media (min-width: 768px) {
    font-size: 3.75rem;
  }

  span {
    ${gradientTextStyles}
  }
`;

export const FourYearHeroLead = styled.p`
  margin: 0;
  color: ${theme.textMuted};
  font-size: 1.125rem;
  line-height: 1.6;
`;

export const FourYearFeedSection = styled.section`
  padding: 0 16px 48px;
`;

export const FourYearFeedInner = styled.div`
  max-width: 48rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const FourYearEmptyState = styled.div`
  ${glassCardStyles}
  padding: 48px 24px;
  border-radius: 1rem;
  text-align: center;
  color: ${theme.textMuted};
  font-size: 0.9375rem;
`;

export const FourYearPaginationWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 16px 64px;
`;

export const FourYearCardRoot = styled.article`
  position: relative;
  ${glassCardStyles}
  overflow: hidden;
  border-radius: 1rem;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: hsl(var(--hp-primary) / 0.4);
  }
`;

export const FourYearCardOverlayLink = styled(Link)`
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: 1rem;
  ${cardLinkNoUnderline}

  span {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

export const FourYearCardGrid = styled.div`
  display: grid;

  @media (min-width: 640px) {
    grid-template-columns: 220px 1fr;
    align-items: stretch;
  }
`;

export const FourYearCardMedia = styled.div`
  position: relative;
  height: 160px;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    hsl(var(--hp-primary) / 0.2) 0%,
    ${theme.bg} 50%,
    hsl(var(--hp-accent) / 0.1) 100%
  );

  @media (min-width: 640px) {
    height: 100%;
    min-height: 176px;
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  ${FourYearCardRoot}:hover & img {
    transform: scale(1.05);
  }
`;

export const FourYearCardMediaGradient = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top right,
    hsl(var(--hp-background) / 0.7) 0%,
    hsl(var(--hp-background) / 0.1) 50%,
    transparent 100%
  );
`;

export const FourYearCardBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

export const FourYearAuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  min-width: 0;
`;

export const FourYearAuthorLink = styled(Link)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  position: relative;
  z-index: 3;
  ${cardLinkNoUnderline}

  img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    box-shadow: 0 0 0 2px hsl(var(--hp-primary) / 0.3);
  }

  strong {
    display: block;
    color: ${theme.foreground};
    font-size: 0.875rem;
    font-weight: ${typography.weights.semibold};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: block;
    color: ${theme.textMuted};
    font-size: 0.75rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover strong {
    color: ${theme.primary};
  }
`;

export const FourYearFeaturedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: hsl(var(--hp-primary) / 0.15);
  color: ${theme.primary};
  font-size: 0.625rem;
  font-weight: ${typography.weights.bold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.3;
  white-space: nowrap;
`;

export const FourYearCardTitle = styled.h2`
  margin: 0 0 8px;
  color: ${theme.foreground};
  font-size: 1.25rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.35;
  transition: color 0.2s ease;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }

  ${FourYearCardRoot}:hover & {
    color: ${theme.primary};
  }
`;

export const FourYearCardExcerpt = styled.p`
  margin: 0 0 16px;
  color: ${theme.textMuted};
  font-size: 0.9375rem;
  line-height: 1.65;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const FourYearTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
`;

export const FourYearTypePill = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  background: hsl(var(--hp-background) / 0.8);
  backdrop-filter: blur(8px);
  color: ${theme.foreground};
  font-size: 0.75rem;
  font-weight: ${typography.weights.semibold};

  .material-icons {
    font-size: 14px;
    color: ${theme.primary};
  }
`;

export const FourYearTag = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: hsl(var(--hp-secondary) / 0.6);
  color: ${theme.textMuted};
  font-size: 0.6875rem;
`;

export const FourYearMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid hsl(var(--hp-border) / 0.4);
  color: ${theme.textMuted};
  font-size: 0.75rem;
`;

export const FourYearMetaDate = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  .material-icons {
    font-size: 14px;
    line-height: 1;
  }
`;

export const FourYearPortfolioSection = styled.section`
  padding: 64px 16px;
  border-top: 1px solid ${theme.border};
`;

export const FourYearPortfolioInner = styled.div`
  max-width: 64rem;
  margin: 0 auto;
`;

export const FourYearPortfolioTitle = styled.h2`
  margin: 0 0 8px;
  color: ${theme.foreground};
  font-size: 1.5rem;
  font-weight: ${typography.weights.bold};

  @media (min-width: 768px) {
    font-size: 1.875rem;
  }
`;

export const FourYearPortfolioLead = styled.p`
  margin: 0 0 32px;
  color: ${theme.textMuted};
  font-size: 1rem;
  line-height: 1.6;
`;

export const FourYearSheetFrame = styled.div`
  display: none;
  overflow: hidden;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 1rem;
  background: hsl(var(--hp-card) / 0.5);

  /* Prod used Bootstrap lg (992px): iframe desktop, link on tablet/mobile. */
  @media (min-width: 992px) {
    display: block;
  }

  iframe {
    display: block;
    width: 100%;
    height: 650px;
    border: 0;
  }
`;

export const FourYearSheetMobile = styled.div`
  display: block;

  @media (min-width: 992px) {
    display: none;
  }
`;

export const FourYearSheetButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  padding: 0 20px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: var(--hp-radius, 0.75rem);
  background: hsl(var(--hp-card) / 0.6);
  color: ${theme.foreground};
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: ${typography.weights.semibold};
  text-decoration: none;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    color 0.2s ease;

  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
    color: ${theme.primary};
    border-color: hsl(var(--hp-primary) / 0.4);
  }

  .material-icons {
    font-size: 18px;
    margin-left: 8px;
  }
`;

export const FourYearSkeletonCard = styled.div`
  height: 180px;
  border-radius: 1rem;
  border: 1px solid hsl(var(--hp-border) / 0.5);
  background: hsl(var(--hp-muted) / 0.35);
  animation: four-year-pulse 1.5s ease-in-out infinite;

  @keyframes four-year-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.55;
    }
  }
`;
